import { useState, useMemo, useEffect } from 'react';
import styles from './form.module.css';
import { useApiClient } from '../useApiClient';

const formConfig = [
  {
    name: 'username', label: 'Username',
    type: 'text', required: true
  },
  {
    name: 'password', label: 'Password',
    type: 'text', required: true
  },
  {
    name: 'base_url', label: 'Base URL',
    type: 'url', required: true
  },
  {
    name: 'api_name', label: 'API Name',
    type: 'text', required: true
  },
  {
    name: 'api_key', label: 'API Key',
    type: 'text', required: true
  },
];

const emptyForm = {
  username: "",
  password: "",
  base_url: "",
  api_name: "",
  api_key: "",
  version: ""
}

const removeFields = (obj, fields) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => !fields.includes(key))
  );

export function FormComponent({
  selectedEntry = null,
  onSaved,
  onCancelEdit
}) {
  const { postWithAuth, patchWithAuth } = useApiClient();
  const [apiName, setApiName] = useState(null);

  const isEditMode = !!selectedEntry;
  const initialFormData = useMemo(() => {
    if (selectedEntry) {
      setApiName(selectedEntry.api_name);
    }
    return selectedEntry ? { ...emptyForm, ...selectedEntry } : emptyForm;
  }, [selectedEntry]);

  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // --- Setting initial form data ---
  useEffect(() => {
    setFormData(initialFormData);
    setError(null);
  }, [initialFormData]);

  // --- Handling input ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- Handling form submission ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      let savedEntry;
      if (isEditMode) {
        const etag = formData.version;
        const cleaned = removeFields(formData, ["created_at", "updated_at", "version"]);
        console.log(cleaned);
        savedEntry = await patchWithAuth(`/credentials/${apiName}`, cleaned, etag);
      } else {
        const cleaned = removeFields(formData, ["version"]);
        savedEntry = await postWithAuth("/credentials", cleaned);
      }
      onSaved?.(savedEntry, { isEditMode });
      if (!isEditMode) {
        setFormData(emptyForm);
      }
    } catch (err) {
      setError(err?.message || "Failed to save entry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.formContainer} onSubmit={handleSubmit}>
      <fieldset className={styles.fieldsetContainer} disabled={submitting}>
        <legend className={styles.containerTitle}>
          {isEditMode ? "Edit API connection" : "Add API connection"}
        </legend>

        {formConfig.map((field) => (
          <div className={styles.formEntry} key={field.name}>
            <label
              className={styles.formLabel}
              htmlFor={field.name}
            >
              {field.label}{field.required && '*'}
            </label>
            <input
              className={styles.formInput}
              type={field.type}
              id={field.name}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              required={field.required}
            />
          </div>
        ))}

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button className={styles.actionButton} type="submit">
            {submitting ? "Saving..." : isEditMode ? "Update" : "Save"}
          </button>

          {isEditMode && (
            <button
              className={styles.actionButton}
              type="button"
              onClick={onCancelEdit}
              style={{ backgroundColor: "var(--color-error" }}
            >
              Cancel
            </button>
          )}
        </div>
      </fieldset>
    </form>
  );
};
